import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { CheckCircle2, Minus, Plus, Loader2, Pencil, Eye, Zap } from "lucide-react";

const BADGES = ["None", "Most Popular", "Best Value", "Recommended"];
const CYCLES = [{ v: "monthly", l: "Monthly" }, { v: "lifetime", l: "Lifetime" }];

const emptyForm = (features) => ({
  id: null, name: "", price: 0, billing_cycle: "monthly", application_limit: "",
  reply_limit: "", company_limit: "", short_description: "", tagline: "", badge: "None",
  status: "active", visible: true,
  features: Object.fromEntries(features.map((f) => [f.key, false])),
});

function planToForm(p) {
  return {
    id: p.id, name: p.name, price: p.price, billing_cycle: p.billing_cycle,
    application_limit: p.application_limit == null ? "" : String(p.application_limit),
    reply_limit: p.reply_limit == null ? "" : String(p.reply_limit),
    company_limit: p.company_limit == null ? "" : String(p.company_limit),
    short_description: p.short_description || "", tagline: p.tagline || "",
    badge: p.badge || "None", status: p.status, visible: p.visible,
    features: { ...p.features },
  };
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function Pill({ ok, label }) {
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${ok ? "text-emerald-600" : "text-slate-400"}`}>
      <span className={`w-2 h-2 rounded-full ${ok ? "bg-emerald-500" : "bg-slate-300"}`} /> {label}
    </span>
  );
}

export default function AdminPlans() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [replyCfg, setReplyCfg] = useState(null);
  const [replyPrice, setReplyPrice] = useState("");
  const [slotCfg, setSlotCfg] = useState(null);
  const [slotPrice, setSlotPrice] = useState("");
  const [savingAddons, setSavingAddons] = useState(false);

  const loadAddonCfg = useCallback(() => {
    api.get("/payments/reply-credits/config").then(({ data }) => {
      setReplyCfg(data);
      setReplyPrice(String(data.price_per_credit));
    }).catch(() => {});
    api.get("/payments/company-slots/config").then(({ data }) => {
      setSlotCfg(data);
      setSlotPrice(String(data.price_per_slot));
    }).catch(() => {});
  }, []);

  const saveAddonPrices = async () => {
    setSavingAddons(true);
    try {
      await api.put("/admin/settings/REPLY_CREDIT_PRICE_INR", { value: replyPrice });
      await api.put("/admin/settings/COMPANY_SLOT_PRICE_INR", { value: slotPrice });
      toast.success("Add-on pricing saved.");
      loadAddonCfg();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingAddons(false);
    }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/plans");
      setPlans(data.items);
      setFeatures(data.features);
      return data;
    } catch {
      toast.error("Could not load plans.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load().then((data) => {
      if (data?.items?.length) setForm(planToForm(data.items[0]));
    });
    loadAddonCfg();
  }, [load, loadAddonCfg]);

  const editPlan = (p) => { setCreating(false); setForm(planToForm(p)); };
  const addPlan = () => { setCreating(true); setForm(emptyForm(features)); };
  const selectFromDropdown = (id) => {
    if (id === "__new__") return addPlan();
    const p = plans.find((x) => x.id === id);
    if (p) editPlan(p);
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleFeature = (key) => setForm((f) => ({ ...f, features: { ...f.features, [key]: !f.features[key] } }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Plan name is required."); return; }
    setSaving(true);
    const cleanLimit = (v) => {
      const s = String(v).trim();
      return (s === "" || s.toLowerCase() === "unlimited") ? null : Number(s);
    };
    const payload = {
      name: form.name.trim(),
      price: Number(form.price) || 0,
      billing_cycle: form.billing_cycle,
      application_limit: cleanLimit(form.application_limit),
      reply_limit: cleanLimit(form.reply_limit),
      company_limit: cleanLimit(form.company_limit),
      badge: form.badge === "None" ? null : form.badge,
      short_description: form.short_description,
      tagline: form.tagline,
      status: form.status,
      visible: form.visible,
      features: form.features,
    };
    try {
      let saved;
      if (creating || !form.id) {
        ({ data: saved } = await api.post("/admin/plans", payload));
        toast.success(`Plan "${saved.name}" created.`);
      } else {
        ({ data: saved } = await api.put(`/admin/plans/${form.id}`, payload));
        toast.success(`Plan "${saved.name}" updated.`);
      }
      const data = await load();
      const match = data?.items?.find((x) => x.id === saved.id);
      if (match) { setCreating(false); setForm(planToForm(match)); }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const del = async (p) => {
    if (p.slug === "free") { toast.error("The Free plan cannot be deleted."); return; }
    if (!window.confirm(`Delete the "${p.name}" plan? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/plans/${p.id}`);
      toast.success(`Plan "${p.name}" deleted.`);
      const data = await load();
      if (data?.items?.length) setForm(planToForm(data.items[0]));
      else setForm(emptyForm(features));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const featureCols = useMemo(() => {
    const mid = Math.ceil(features.length / 2);
    return [features.slice(0, mid), features.slice(mid)];
  }, [features]);

  if (loading) {
    return <div className="py-24 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-amber-500" /></div>;
  }

  return (
    <div data-testid="admin-plans-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-display text-slate-900">Plans &amp; Pricing</h1>
          <p className="text-slate-500 mt-1.5">Create and manage candidate plans, feature access and visibility.</p>
        </div>
        <div className="flex items-center gap-3">
          <Button data-testid="preview-pricing-button" variant="outline" onClick={() => navigate("/app/pricing")}
            className="border-amber-400 text-amber-700 hover:bg-amber-50 font-semibold">
            <Eye className="w-4 h-4 mr-2" /> Preview Pricing Page
          </Button>
          <Button data-testid="add-plan-button" onClick={addPlan} className="bg-amber-500 text-black hover:bg-amber-400 font-semibold">
            <Plus className="w-4 h-4 mr-2" /> Add Plan
          </Button>
        </div>
      </div>

      <div className="grid xl:grid-cols-[1fr_420px] gap-6 items-start">
        {/* Left: table + matrix + add-on pricing */}
        <div className="space-y-6 min-w-0">
          {/* Plans table */}
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100">
              <h2 className="text-lg font-bold text-slate-900 font-display">Plans ({plans.length})</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] text-sm">
                <thead>
                  <tr className="text-left text-xs font-semibold text-slate-500 border-b border-slate-100">
                    <th className="px-6 py-3">Plan Name</th>
                    <th className="px-3 py-3">Price</th>
                    <th className="px-3 py-3">Billing Cycle</th>
                    <th className="px-3 py-3">Application Limit</th>
                    <th className="px-3 py-3">Reply Limit</th>
                    <th className="px-3 py-3">Company Limit</th>
                    <th className="px-3 py-3">Status</th>
                    <th className="px-3 py-3">Visibility</th>
                    <th className="px-3 py-3">Updated On</th>
                    <th className="px-3 py-3 pr-6">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {plans.map((p) => (
                    <tr key={p.id} data-testid={`plan-row-${p.slug}`} className="border-b border-slate-50 hover:bg-slate-50/60">
                      <td className="px-6 py-3.5 font-semibold text-slate-900">{p.name}</td>
                      <td className="px-3 py-3.5 text-slate-700">₹{p.price.toLocaleString("en-IN")}</td>
                      <td className="px-3 py-3.5 text-slate-500 capitalize">{p.billing_cycle}</td>
                      <td className="px-3 py-3.5 text-slate-700">{p.application_limit == null ? "Unlimited" : p.application_limit}</td>
                      <td className="px-3 py-3.5 text-slate-700" data-testid={`plan-replies-${p.slug}`}>
                        {p.reply_limit == null ? "Unlimited" : `${p.reply_limit} / mo`}
                      </td>
                      <td className="px-3 py-3.5 text-slate-700" data-testid={`plan-companies-${p.slug}`}>
                        {p.company_limit == null ? "Unlimited" : p.company_limit}
                      </td>
                      <td className="px-3 py-3.5"><Pill ok={p.status === "active"} label={p.status === "active" ? "Active" : "Inactive"} /></td>
                      <td className="px-3 py-3.5"><Pill ok={p.visible} label={p.visible ? "Visible" : "Hidden"} /></td>
                      <td className="px-3 py-3.5 text-slate-500">{fmtDate(p.updated_at)}</td>
                      <td className="px-3 py-3.5 pr-6">
                        <div className="flex items-center gap-2">
                          <button data-testid={`plan-edit-${p.slug}`} onClick={() => editPlan(p)}
                            className="px-3 py-1.5 rounded-lg border border-slate-200 text-slate-700 text-xs font-semibold hover:bg-slate-100 inline-flex items-center gap-1.5">
                            <Pencil className="w-3.5 h-3.5" /> Edit
                          </button>
                          {p.slug !== "free" && (
                            <button data-testid={`plan-delete-${p.slug}`} onClick={() => del(p)}
                              className="px-2 py-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 text-xs font-semibold">
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Feature matrix */}
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="plan-feature-matrix">
            <div className="px-6 py-4 border-b border-slate-100">
              <h2 className="text-lg font-bold text-slate-900 font-display">Plan Feature Matrix</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[560px] text-sm">
                <thead>
                  <tr className="text-left border-b border-slate-100">
                    <th className="px-6 py-3 font-semibold text-slate-600">Features</th>
                    {plans.map((p) => <th key={p.id} className="px-4 py-3 text-center font-semibold text-slate-700">{p.name}</th>)}
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-slate-50">
                    <td className="px-6 py-3 text-slate-600">Applications</td>
                    {plans.map((p) => (
                      <td key={p.id} className="px-4 py-3 text-center text-slate-700">
                        {p.application_limit == null ? "Unlimited" : p.billing_cycle === "monthly" ? `${p.application_limit} / month` : p.application_limit}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b border-slate-50" data-testid="matrix-reply-credits-row">
                    <td className="px-6 py-3 text-slate-600">Reply Credits / month</td>
                    {plans.map((p) => (
                      <td key={p.id} className="px-4 py-3 text-center text-slate-700" data-testid={`matrix-replies-${p.slug}`}>
                        {p.reply_limit == null ? "Unlimited" : p.reply_limit}
                      </td>
                    ))}
                  </tr>
                  <tr className="border-b border-slate-50" data-testid="matrix-companies-row">
                    <td className="px-6 py-3 text-slate-600">Saved Companies (lifetime)</td>
                    {plans.map((p) => (
                      <td key={p.id} className="px-4 py-3 text-center text-slate-700" data-testid={`matrix-companies-${p.slug}`}>
                        {p.company_limit == null ? "Unlimited" : p.company_limit}
                      </td>
                    ))}
                  </tr>
                  {features.map((f) => (
                    <tr key={f.key} className="border-b border-slate-50">
                      <td className="px-6 py-3 text-slate-600">{f.label}</td>
                      {plans.map((p) => (
                        <td key={p.id} className="px-4 py-3 text-center">
                          {p.features[f.key]
                            ? <CheckCircle2 className="w-4 h-4 text-emerald-500 mx-auto" />
                            : <Minus className="w-4 h-4 text-slate-300 mx-auto" />}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Add-on pricing (top-up packs) */}
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="reply-credits-admin-card">
            <div className="px-6 py-4 border-b border-slate-100 flex items-start gap-2.5">
              <Zap className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
              <div>
                <h2 className="text-lg font-bold text-slate-900 font-display">Add-on Pricing</h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  One fixed price per add-on unit, across all plans. Reply and company counts are set per plan above
                  (blank = Unlimited). Users buy top-up packs once their plan's quota runs out; purchased units never expire.
                </p>
              </div>
            </div>
            <div className="p-6">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-slate-500">Price per reply credit (₹)</label>
                  <input data-testid="reply-price-input" type="number" min="1" value={replyPrice}
                    onChange={(e) => setReplyPrice(e.target.value)}
                    className="mt-1.5 w-full h-11 rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-amber-500" />
                  <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="reply-pack-preview">
                    {(replyCfg?.bundles || []).map((b) => (
                      <span key={b.quantity}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-1.5 text-xs font-semibold text-slate-700">
                        {b.quantity} replies · ₹{b.quantity * (Number(replyPrice) || 0)}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500">Price per company slot (₹)</label>
                  <input data-testid="slot-price-input" type="number" min="1" value={slotPrice}
                    onChange={(e) => setSlotPrice(e.target.value)}
                    className="mt-1.5 w-full h-11 rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-amber-500" />
                  <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="slot-pack-preview">
                    {(slotCfg?.packs || []).map((p) => (
                      <span key={p.quantity}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-1.5 text-xs font-semibold text-slate-700">
                        {p.quantity} {p.quantity === 1 ? "slot" : "slots"} · ₹{p.quantity * (Number(slotPrice) || 0)}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="mt-4 flex justify-end">
                <Button data-testid="reply-settings-save" onClick={saveAddonPrices} disabled={savingAddons}
                  className="bg-amber-500 text-black hover:bg-amber-400 font-semibold">
                  {savingAddons ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Add-on Prices"}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Right: edit panel */}
        {form && (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 xl:sticky xl:top-24" data-testid="plan-edit-panel">
            <h2 className="text-lg font-bold text-slate-900 font-display">{creating ? "Add Plan" : "Edit Plan"}</h2>

            {!creating && (
              <div className="mt-4">
                <label className="text-xs font-semibold text-slate-500">Select Plan</label>
                <select data-testid="edit-select-plan" value={form.id || ""} onChange={(e) => selectFromDropdown(e.target.value)}
                  className="mt-1.5 w-full h-11 rounded-lg border border-slate-300 bg-white px-3 text-sm outline-none focus:border-amber-500">
                  {plans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  <option value="__new__">+ Create new plan…</option>
                </select>
              </div>
            )}

            <div className="mt-4 grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-slate-500">Plan Name</label>
                <input data-testid="edit-name" value={form.name} onChange={(e) => setField("name", e.target.value)}
                  className="mt-1.5 w-full h-11 rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-amber-500" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500">Price (INR)</label>
                <input data-testid="edit-price" type="number" min="0" value={form.price} onChange={(e) => setField("price", e.target.value)}
                  className="mt-1.5 w-full h-11 rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-amber-500" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500">Billing Cycle</label>
                <select data-testid="edit-billing" value={form.billing_cycle} onChange={(e) => setField("billing_cycle", e.target.value)}
                  className="mt-1.5 w-full h-11 rounded-lg border border-slate-300 bg-white px-3 text-sm outline-none focus:border-amber-500">
                  {CYCLES.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500">Application Limit</label>
                <input data-testid="edit-limit" value={form.application_limit} placeholder="Unlimited"
                  onChange={(e) => setField("application_limit", e.target.value)}
                  className="mt-1.5 w-full h-11 rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-amber-500" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500">Reply Limit / month</label>
                <input data-testid="edit-reply-limit" value={form.reply_limit} placeholder="Unlimited"
                  onChange={(e) => setField("reply_limit", e.target.value)}
                  className="mt-1.5 w-full h-11 rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-amber-500" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500">Company Limit (lifetime)</label>
                <input data-testid="edit-company-limit" value={form.company_limit} placeholder="Unlimited"
                  onChange={(e) => setField("company_limit", e.target.value)}
                  className="mt-1.5 w-full h-11 rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-amber-500" />
              </div>
            </div>

            <div className="mt-4">
              <label className="text-xs font-semibold text-slate-500">Short Description</label>
              <textarea data-testid="edit-description" rows={2} value={form.short_description}
                onChange={(e) => setField("short_description", e.target.value)}
                className="mt-1.5 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-amber-500 resize-none" />
            </div>

            <div className="mt-4 grid grid-cols-2 gap-4 items-start">
              <div>
                <label className="text-xs font-semibold text-slate-500">Plan Badge</label>
                <select data-testid="edit-badge" value={form.badge} onChange={(e) => setField("badge", e.target.value)}
                  className="mt-1.5 w-full h-11 rounded-lg border border-slate-300 bg-white px-3 text-sm outline-none focus:border-amber-500">
                  {BADGES.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
              <div className="space-y-3 pt-1">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Status</span>
                  <div className="flex items-center gap-2">
                    <Switch data-testid="edit-status" checked={form.status === "active"}
                      onCheckedChange={(v) => setField("status", v ? "active" : "inactive")} />
                    <span className="text-sm font-medium text-slate-700">{form.status === "active" ? "Active" : "Inactive"}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Show on Pricing Page</span>
                  <div className="flex items-center gap-2">
                    <Switch data-testid="edit-visible" checked={form.visible} onCheckedChange={(v) => setField("visible", v)} />
                    <span className="text-sm font-medium text-slate-700">{form.visible ? "Visible" : "Hidden"}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-5">
              <h3 className="font-bold text-slate-900 text-sm">Feature Access</h3>
              <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2.5">
                {featureCols.map((col, ci) => (
                  <div key={ci} className="space-y-2.5">
                    {col.map((f) => (
                      <label key={f.key} data-testid={`edit-feature-${f.key}`}
                        className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
                        <input type="checkbox" checked={!!form.features[f.key]} onChange={() => toggleFeature(f.key)}
                          className="mt-0.5 w-4 h-4 rounded border-slate-300 text-amber-500 accent-amber-500" />
                        <span>{f.label}</span>
                      </label>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 flex items-center gap-3">
              <Button variant="outline" data-testid="edit-cancel"
                onClick={() => { if (plans[0]) editPlan(plans[0]); }}
                className="flex-1 border-slate-300 text-slate-600 hover:bg-slate-50 font-semibold">
                Cancel
              </Button>
              <Button data-testid="edit-save" onClick={save} disabled={saving}
                className="flex-1 bg-amber-500 text-black hover:bg-amber-400 font-semibold">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : creating ? "Create Plan" : "Save Changes"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
